import React, { useState, useEffect } from "react";
import QuestionStep, { QuestionStepSkeleton } from "./QuestionStep";
import ResultStep from "./ResultStep";
import "../../styles/Quiz/Quiz.css";
import config from "../../config/config";
import {
  trackQuizStart,
  trackQuestionResponse,
  trackQuizComplete,
} from "../../utils/analytics";

export default function Quiz({ isOpen, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizData, setQuizData] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && !quizData) {
      fetchQuizFromAPI();
    }
  }, [isOpen]);

  const fetchQuizFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${config.calculatorUrl}quiz/start/`, {
        headers: {
          Authorization: `${process.env.REACT_APP_API_KEY}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setQuizData(data);
      trackQuizStart({ sessionId: data.session_id });
    } catch (err) {
      console.error("Failed to fetch quiz data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleSelectOption = (questionId, optionIndex) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionIndex }));
  };

  const handleNext = async () => {
    if (!currentQuestion || selectedOption === undefined) return;

    const originalQuestion = quiz.quiz_questions.find(
      (q) => q.question_id === currentQuestion.id,
    );
    const selectedChoice = originalQuestion.question_choices[selectedOption];

    const singleAnswerPayload = {
      quiz_response: quizData.session_id,
      question: currentQuestion.id,
      choice: selectedChoice.choice_id,
      company: selectedChoice.recommended_company,
    };

    fetch(`${config.calculatorUrl}quiz/answer/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${process.env.REACT_APP_API_KEY}`,
      },
      body: JSON.stringify(singleAnswerPayload),
      keepalive: true,
    })
      .then((res) => {
        if (!res.ok) console.error(`Failed to submit answer: ${res.status}`);
      })
      .catch((err) => console.error("Quiz answer submission error:", err));

    trackQuestionResponse({
      questionNumber: stepIndex + 1,
      questionId: currentQuestion.id,
      questionText: currentQuestion.question,
      answerText: selectedChoice.choice,
      sessionId: quizData.session_id,
      recommendedCompanyName: selectedChoice.recommended_company_name,
    });

    if (!isLastQuestion) {
      setStepIndex((prev) => prev + 1);
      return;
    }

    try {
      setIsSubmitting(true);

      const allAnswersPayload = quiz.quiz_questions.map((q) => {
        const optionIndex =
          q.question_id === currentQuestion.id
            ? selectedOption
            : answers[q.question_id];
        const choiceData = q.question_choices[optionIndex];
        return {
          question: q.question_id,
          choice: choiceData.choice_id,
          company: choiceData.recommended_company,
        };
      });

      const resultResponse = await fetch(
        `${config.calculatorUrl}quiz/result/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${process.env.REACT_APP_API_KEY}`,
          },
          body: JSON.stringify({
            quiz_response: quizData.session_id,
            answers: allAnswersPayload,
          }),
        },
      );

      if (!resultResponse.ok) {
        throw new Error(`Failed to fetch result: ${resultResponse.status}`);
      }

      const resultJson = await resultResponse.json();
      setResultData({
        ...resultJson,
        benefits: [...(resultJson.benefits || [])].sort(
          (a, b) => a.order_index - b.order_index,
        ),
      });

      trackQuizComplete({
        sessionId: quizData.session_id,
        company: resultJson.company_name,
      });

      setStepIndex((prev) => prev + 1);
    } catch (err) {
      console.error("Quiz submission error:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => setStepIndex((prev) => Math.max(prev - 1, 0));

  const handleClose = () => {
    setStepIndex(0);
    setAnswers({});
    setResultData(null);
    setQuizData(null);
    onClose?.();
  };

  // --- LOADING STATE ---
  if (loading) {
    return (
      <div className="quiz-overlay" role="dialog" aria-modal="true">
        <div className="quiz-card">
          <div className="quiz-header" style={{ backgroundColor: "#f7f8fa" }}>
            <div
              className="skeleton-pulse"
              style={{ height: "24px", width: "200px", borderRadius: "6px" }}
            />
          </div>
          <QuestionStepSkeleton />
        </div>
      </div>
    );
  }

  // --- ERROR STATE ---
  if (error) {
    return (
      <div className="quiz-overlay" role="dialog" aria-modal="true">
        <div className="quiz-card">
          <div
            className="quiz-error"
            style={{ padding: "48px", textAlign: "center" }}
          >
            <p>Failed: {error}</p>
            <button
              onClick={handleClose}
              className="quiz-btn quiz-btn--primary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!quizData || !quizData.quiz) return null;

  const { quiz } = quizData;

  const mappedQuiz = {
    headerTitle: quiz.quiz_card_title,
    headerBgColor: quiz.quiz_card_title_color || "#ffffff",
    questions: quiz.quiz_questions.map((q) => ({
      id: q.question_id,
      question: q.question,
      options: q.question_choices.map((choice) => choice.choice),
    })),
  };

  const isLastQuestion = stepIndex === mappedQuiz.questions.length - 1;
  const isResultStep = stepIndex === mappedQuiz.questions.length;

  const currentQuestion = !isResultStep
    ? mappedQuiz.questions[stepIndex]
    : null;
  const selectedOption = currentQuestion
    ? answers[currentQuestion.id]
    : undefined;

  return (
    <div className="quiz-overlay" role="dialog" aria-modal="true">
      <div className="quiz-card">
        <div
          className="quiz-header"
          style={{ backgroundColor: mappedQuiz.headerBgColor }}
        >
          <span className="quiz-header__title">{mappedQuiz.headerTitle}</span>
          <button
            className="quiz-header__close"
            onClick={handleClose}
            aria-label="Close quiz"
          >
            ✕
          </button>
        </div>

        {isResultStep ? (
          <ResultStep
            resultData={resultData}
            sessionId={quizData.session_id}
            onClose={handleClose}
          />
        ) : (
          <QuestionStep
            index={stepIndex}
            question={currentQuestion}
            selectedOption={selectedOption}
            onSelectOption={handleSelectOption}
            onNext={handleNext}
            onBack={handleBack}
            canGoBack={stepIndex > 0}
            isLastQuestion={isLastQuestion}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
}
